import api, { getApiErrors } from '@app/services/api';
import type {
	AINumberAddParams,
	AINumberAddResponse,
	AINumberDeleteParams,
	AINumberDeleteResponse,
	AINumberEditParams,
	AINumberEditResponse,
	AINumberListParams,
	ApiError,
} from '@app/services/api.types';
import { fn } from "@app/utils";
import { apiEndpoints, defaultErrorMessage } from '@app/utils/constants';
import { useMutation } from '@tanstack/react-query';
import type { AxiosError, AxiosResponse } from 'axios';
import useApp from '../useApp';

export const createNumber = async (params: AINumberAddParams): Promise<AINumberAddResponse> => {
	const url = apiEndpoints.aiNumbers.add;
	const response = await api.post(url, params);
	return response?.data?.data;
};

export const updateNumber = async (params: AINumberEditParams): Promise<AINumberEditResponse> => {
	const url = apiEndpoints.aiNumbers.update(params.id);
	const response = await api.put(url, params);
	return response?.data?.data;
};

export const removeNumber = async (
	params: AINumberDeleteParams
): Promise<AINumberDeleteResponse> => {
	const url = apiEndpoints.aiNumbers.delete(params.id);
	const response = await api.delete(url);
	return response?.data?.data;
};

export const exportNumbers = async (params: AINumberListParams) => {
	const url = apiEndpoints.aiNumbers.getListExport(params);
	const response = await api.get<Blob>(url, {
		responseType: 'blob',
	});
	return response;
};

const useNumberActions = () => {
	const { message } = useApp();

	const addNumber = useMutation<AINumberAddResponse, AxiosError<ApiError>, AINumberAddParams>({
		mutationFn: createNumber,
		onSuccess: () => {
			message.success({
				type: 'success',
				content: 'Number added successfully',
			});
		},
		onError: (error: AxiosError<ApiError>) => {
			const response = error.response?.data;
			const errorMessage = getApiErrors(response?.message, true);
			message.error({
				type: 'error',
				content: errorMessage || defaultErrorMessage,
			});
		},
		meta: {
			invalidates: [['ai-number', 'list']],
		},
	});

	const editNumber = useMutation<AINumberEditResponse, AxiosError<ApiError>, AINumberEditParams>({
		mutationFn: updateNumber,
		onSuccess: () => {
			message.success({
				type: 'success',
				content: 'Number updated successfully',
			});
		},
		onError: (error: AxiosError<ApiError>) => {
			const response = error.response?.data;
			const errorMessage = getApiErrors(response?.error, true);
			message.error({
				type: 'error',
				content: errorMessage || defaultErrorMessage,
			});
		},
		meta: {
			invalidates: [['ai-number', 'list']],
		},
	});

	const deleteNumber = useMutation<
		AINumberDeleteResponse,
		AxiosError<ApiError>,
		AINumberDeleteParams
	>({
		mutationFn: removeNumber,
		onSuccess: () => {
			message.success({
				type: 'success',
				content: 'Number deleted successfully',
			});
		},
		onError: (error: AxiosError<ApiError>) => {
			const response = error.response?.data;
			const errorMessage = getApiErrors(response?.error, true);
			message.error({
				type: 'error',
				content: errorMessage || defaultErrorMessage,
			});
		},
		meta: {
			invalidates: [['ai-number', 'list']],
		},
	});

	const exportNumbersMutation = useMutation<
		AxiosResponse<Blob>,
		AxiosError<ApiError>,
		AINumberListParams
	>({
		mutationFn: exportNumbers,
		onSuccess: (res) => {
			const match = res.headers['content-disposition'].match(/filename\s*=\s*"(.+)"/i);
			const filename = match?.[1] || 'agents.csv';
			fn.downloadFileFromBlob({
				blob: res.data,
				fileName: filename,
			});
		},
		onError: (error: AxiosError<ApiError>) => {
			const response = error.response?.data;
			const errorMessage = getApiErrors(response?.data, true);
			message.error({
				type: 'error',
				content: errorMessage || defaultErrorMessage,
			});
		},
	});

	return {
		addNumber,
		editNumber,
		deleteNumber,
		exportNumbers: exportNumbersMutation,
	};
};

export default useNumberActions;
